import { ChangeEvent, FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { PaperclipIcon, SendIcon, SparklesIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChatMessage, ChatMessageData } from "@/components/ChatMessage";
import { useClients } from "@/hooks";
import { apiClient } from "@/lib/api";
import { useParams, useNavigate } from "react-router-dom";
import { useConversation, useInvalidateConversations } from "@/hooks/useReportConversations";

const createMessageId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

const reportTemplates = [
  "Monthly Spending Company Report",
  "Quarterly Spending Company Report",
  "On Demand Company Report",
  "Unpaid Invoices Report",
  "Employee Spendings Report",
  "Appliances Report",
];

export const ReportGenerationSection = (): JSX.Element => {
  const { clients, isLoading: clientsLoading, error: clientsError } = useClients();
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const invalidateConversations = useInvalidateConversations();

  const { data: conversationData } = useConversation(conversationId || null);

  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [currentMessage, setCurrentMessage] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [attachedFileName, setAttachedFileName] = useState<string | null>(null);
  const [clientRequiredError, setClientRequiredError] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Load conversation data when ID changes or data fetches
  useEffect(() => {
    if (conversationId && conversationData) {
      setSessionId(conversationData.id);
      if (conversationData.client_id) {
        setSelectedClientId(conversationData.client_id);
      }

      const loadedMessages: ChatMessageData[] = conversationData.messages.map(msg => ({
        id: msg.id.toString(),
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.created_at),
        sqlQuery: msg.sql_query || undefined,
        reportData: msg.result_data as Array<Record<string, unknown>> | undefined,
        reportColumns: msg.result_columns || undefined,
        // Reconstruct agent logic if needed, simplify for now
        agentName: msg.role === 'agent' ? 'Report Agent' : undefined,
      }));
      setMessages(loadedMessages);
    } else if (!conversationId) {
      // Reset state for new conversation
      setMessages([]);
      setSessionId(null);
      setSelectedClientId("");
      setAttachedFileName(null);
      setError(null);
    }
  }, [conversationId, conversationData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleClearConversation = (): void => {
    setMessages([]);
    setSessionId(null);
    setError(null);
    setAttachedFileName(null);
    setSelectedClientId("");
    setClientRequiredError(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAttachmentButtonClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleAttachmentChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0] ?? null;
    setAttachedFileName(file ? file.name : null);
  };

  const handleRemoveAttachment = (): void => {
    setAttachedFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSendMessage = async (
    input?: string,
    { resetSession = false }: { resetSession?: boolean } = {},
  ): Promise<void> => {
    const trimmedMessage = (input ?? currentMessage).trim();

    if (!selectedClientId) {
      setClientRequiredError(true);
      return;
    }

    if (!trimmedMessage) {
      setError("Please enter a message before sending.");
      return;
    }

    setClientRequiredError(false);

    if (resetSession) {
      handleClearConversation();
    }

    const outgoingSessionId = resetSession ? null : sessionId;

    const userMessage: ChatMessageData = {
      id: createMessageId(),
      role: "user",
      content: trimmedMessage,
      timestamp: new Date(),
    };

    const placeholderAgentMessage: ChatMessageData = {
      id: createMessageId(),
      role: "agent",
      content: "",
      timestamp: new Date(),
      isLoading: true,
      agentName: "Report Agent",
    };

    setMessages((previous) => [...previous, userMessage, placeholderAgentMessage]);
    setCurrentMessage("");
    setError(null);
    setIsGenerating(true);

    try {
      const payload = await apiClient.post<{
        success: boolean;
        message: string;
        session_id: string;
        conversation_id?: string;
        sql_query?: string;
        data?: any[];
        columns?: any[];
        error?: string;
        can_retry?: boolean;
        steps?: Array<{ tool: string; status: string; description?: string }>;
      }>('/reports/chat', {
        message: trimmedMessage,
        session_id: outgoingSessionId,
        client_id: selectedClientId,
      });

      // Navigate to confirmed conversation URL if this was a new conversation
      if (payload.conversation_id && payload.conversation_id !== conversationId) {
        invalidateConversations();
        navigate(`/reports/${payload.conversation_id}`, { replace: true });
      }

      // Create retry handler for this specific message
      const handleRetry = (): void => {
        // Remove the failed message and retry with the same input
        setMessages((prev) => prev.filter((msg) => msg.id !== placeholderAgentMessage.id));
        void handleSendMessage(trimmedMessage);
      };

      const agentMessage: ChatMessageData = {
        id: placeholderAgentMessage.id,
        role: "agent",
        content: payload.message || "",
        timestamp: new Date(),
        sqlQuery: payload.sql_query ?? undefined,
        reportData: payload.data ?? undefined,
        reportColumns: payload.columns ?? undefined,
        error: payload.success ? undefined : payload.error ?? "Report generation failed",
        isLoading: false,
        canRetry: payload.can_retry ?? false,
        onRetry: payload.can_retry ? handleRetry : undefined,
        steps: payload.steps?.map(s => ({
          tool: s.tool,
          status: s.status as "completed" | "failed",
          description: s.description,
        })),
      };

      if (!payload.success) {
        setError(payload.error || "Report generation failed");
      }

      setSessionId(payload.session_id);

      setMessages((previous) =>
        previous.map((message) => (message.id === placeholderAgentMessage.id ? agentMessage : message)),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setError(message);

      // Create retry handler for network/API errors
      const handleRetry = (): void => {
        setMessages((prev) => prev.filter((msg) => msg.id !== placeholderAgentMessage.id));
        void handleSendMessage(trimmedMessage);
      };

      const errorAgentMessage: ChatMessageData = {
        id: placeholderAgentMessage.id,
        role: "agent",
        content: message,
        timestamp: new Date(),
        error: message,
        isLoading: false,
        canRetry: true,
        onRetry: handleRetry,
      };

      setMessages((previous) =>
        previous.map((msg) => (msg.id === placeholderAgentMessage.id ? errorAgentMessage : msg)),
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void handleSendMessage();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSendMessage();
    }
  };

  return (
    <section className="flex h-full w-full flex-col px-0 py-6">
      <div className="mx-auto flex h-full w-full flex-col px-6">
        <div className="flex items-center justify-center py-4">
          <h2 className="text-2xl font-semibold text-neutral-950">What report do you need?</h2>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="flex h-full flex-col rounded-2xl mx-auto">
            {messages.length > 0 && (
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="flex flex-col gap-4">
                  {messages.map((message) => (
                    <ChatMessage key={message.id} message={message} />
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            )}

            <div className="sticky bottom-0 px-6 py-6">
              <form className="flex flex-col gap-2 w-full max-w-3xl mx-auto" onSubmit={handleSubmit}>
                <div className="flex w-full flex-wrap items-center gap-3 rounded-full bg-neutral-100 px-4 py-2 shadow-sm">
                  <input ref={fileInputRef} type="file" hidden onChange={handleAttachmentChange} />
                  <Input
                    placeholder={sessionId ? "Refine the current report" : "Describe the report you need"}
                    value={currentMessage}
                    onChange={(event) => setCurrentMessage(event.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isGenerating}
                    className="order-1 flex-1 border-none bg-transparent px-0 text-base shadow-none focus-visible:ring-0"
                  />
                  <div className="order-3 flex w-full items-center justify-between gap-2 sm:order-2 sm:w-auto">
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-full"
                        onClick={handleAttachmentButtonClick}
                        disabled={isGenerating}
                      >
                        <PaperclipIcon className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 rounded-full"
                            disabled={isGenerating}
                          >
                            <SparklesIcon className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-64">
                          {reportTemplates.map((template) => (
                            <DropdownMenuItem
                              key={template}
                              onSelect={(event) => {
                                event.preventDefault();
                                void handleSendMessage(template, { resetSession: true });
                              }}
                              className="text-sm"
                            >
                              {template}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="hidden h-6 w-px bg-neutral-200 sm:block" />
                    <div className="flex items-center gap-2">
                      <div className="w-44">
                        <Select
                          value={selectedClientId}
                          onValueChange={(value: string) => {
                            setSelectedClientId(value);
                            setClientRequiredError(false);
                          }}
                          disabled={clientsLoading}
                        >
                          <SelectTrigger
                            className={`h-10 w-full border px-3 focus-visible:ring-0 ${clientRequiredError ? "border-red-500 focus-visible:ring-red-200" : "border-transparent"
                              }`}
                            aria-required="true"
                            aria-invalid={clientRequiredError}
                          >
                            <SelectValue placeholder={clientsLoading ? "Loading clients..." : "Select client"} />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.name}
                              </SelectItem>
                            ))}
                            {clients.length === 0 && !clientsLoading && (
                              <SelectItem value="no-clients" disabled>
                                {clientsError || "No clients found"}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="submit"
                        size="icon"
                        variant="blue"
                        className="h-11 w-11 rounded-full"
                        disabled={isGenerating || clientsLoading || !selectedClientId}
                      >
                        {isGenerating ? (
                          <span className="text-xs font-medium">...</span>
                        ) : (
                          <SendIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                {clientRequiredError && (
                  <p className="px-2 text-xs font-medium text-red-500 text-center">
                    Please select a client before generating a report.
                  </p>
                )}
                <p className="px-2 text-xs text-neutral-400 text-center">AI can make mistakes. Check important info.</p>
              </form>

              {attachedFileName && (
                <div className="flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs text-neutral-600">
                  <span className="truncate">Attached: {attachedFileName}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={handleRemoveAttachment}
                  >
                    <XIcon className="h-3 w-3" />
                  </Button>
                </div>
              )}

              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
